import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import SalaryPortabilityApi from '~/repositories/salaryPortability';
import * as ActionTypes from './actionTypes';

import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

export type SetSalaryPortabilityErrorAction = {
  type: string;
  payload: Error | null;
};

export type SetSalaryPortabilitySuccessAction = {
  type: string;
  payload: string | null;
};

export const setSalaryPortabilityError =
  (error: SetSalaryPortabilityErrorAction['payload']) =>
  (
    dispatch: Dispatch<
      SetSalaryPortabilityErrorAction | SetSalaryPortabilitySuccessAction
    >
  ) => {
    dispatch({
      type: ActionTypes.SET_SALARY_PORTABILITY_ERROR,
      payload: error,
    });
    dispatch({
      type: ActionTypes.SET_SALARY_PORTABILITY_SUCCESS,
      payload: null,
    });
  };

export const setSalaryPortabilitySuccess =
  (success: SetSalaryPortabilitySuccessAction['payload']) =>
  (
    dispatch: Dispatch<
      SetSalaryPortabilityErrorAction | SetSalaryPortabilitySuccessAction
    >
  ) => {
    dispatch({
      type: ActionTypes.SET_SALARY_PORTABILITY_ERROR,
      payload: null,
    });
    dispatch({
      type: ActionTypes.SET_SALARY_PORTABILITY_SUCCESS,
      payload: success,
    });
  };

export type SetSalaryPortabilityFavoredAction = {
  type: string;
  payload: models.ExternalContact | null;
};

export const getSalaryPortabilityFavored =
  (): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    | SetSalaryPortabilityErrorAction
    | LoadingAction
    | SetSalaryPortabilityFavoredAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const { favored } = await SalaryPortabilityApi.getFavored();
      dispatch({
        payload: favored,
        type: ActionTypes.SET_SALARY_PORTABILITY_FAVORED,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSalaryPortabilityError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const createSalaryPortabilityFavored =
  (
    favored: models.ExternalContact | models.PixContact
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    | SetSalaryPortabilityErrorAction
    | LoadingAction
    | SetSalaryPortabilitySuccessAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await SalaryPortabilityApi.createFavored(favored);
      dispatch(setSalaryPortabilitySuccess('CREATED'));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSalaryPortabilityError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const updateSalaryPortabilityFavored =
  (
    favored: Omit<models.ExternalContact, 'accountFavoredId' | 'pixKey'>
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    | SetSalaryPortabilityErrorAction
    | LoadingAction
    | SetSalaryPortabilitySuccessAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await SalaryPortabilityApi.updateFavored(favored);
      dispatch(setSalaryPortabilitySuccess('UPDATED'));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSalaryPortabilityError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const deleteSalaryPortabilityFavored =
  (): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    | SetSalaryPortabilityErrorAction
    | LoadingAction
    | SetSalaryPortabilitySuccessAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await SalaryPortabilityApi.deleteFavored();
      dispatch(setSalaryPortabilitySuccess('DELETED'));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSalaryPortabilityError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
